import type Konva from 'konva';
import { type RefObject, useEffect, useRef, useState } from 'react';
import { useCleanedBitmap } from '~/features/source/SharedBitmapContext';
import { useStore } from '~/store';

const LOUPE_SIZE = 132;
const SAMPLE = 16;
const ZOOM = LOUPE_SIZE / SAMPLE;
const HALF = SAMPLE / 2;
const OFFSET = 24;

type LoupeProps = {
  containerRef: RefObject<HTMLDivElement | null>;
  stageRef: RefObject<Konva.Stage | null>;
};

function paint(canvas: HTMLCanvasElement, bitmap: ImageBitmap, src: { x: number; y: number }) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.imageSmoothingEnabled = false;
  ctx.clearRect(0, 0, LOUPE_SIZE, LOUPE_SIZE);
  const sx = Math.round(src.x) - HALF;
  const sy = Math.round(src.y) - HALF;
  ctx.drawImage(bitmap, sx, sy, SAMPLE, SAMPLE, 0, 0, LOUPE_SIZE, LOUPE_SIZE);
  ctx.strokeStyle = 'rgba(255,255,255,0.85)';
  ctx.lineWidth = 1;
  ctx.strokeRect(HALF * ZOOM, HALF * ZOOM, ZOOM, ZOOM);
}

/** Magnifier overlay shown while a precision-pick mode is active. Samples the
 *  cleaned bitmap around the cursor's source-pixel position and draws it
 *  zoomed (pixelated) inside a circular DOM patch. */
export function Loupe({ containerRef, stageRef }: LoupeProps) {
  const drawMode = useStore((s) => s.editor.drawMode);
  const bitmap = useCleanedBitmap();
  const dims = useStore((s) => s.source.dimensions);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const srcRef = useRef<{ x: number; y: number } | null>(null);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);

  const active = drawMode === 'keyRegion' && bitmap !== null && dims !== null;

  useEffect(() => {
    if (!active) {
      setPos(null);
      srcRef.current = null;
      return;
    }
    const container = containerRef.current;
    const stage = stageRef.current;
    if (!container || !stage) return;

    let rect = container.getBoundingClientRect();
    const refreshRect = () => {
      rect = container.getBoundingClientRect();
    };
    const ro = new ResizeObserver(refreshRect);
    ro.observe(container);

    const onMove = (e: PointerEvent) => {
      stage.setPointersPositions(e);
      const src = stage.getRelativePointerPosition();
      if (!src) return;
      srcRef.current = src;
      setPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
      const canvas = canvasRef.current;
      if (canvas) paint(canvas, bitmap, src);
    };
    const onLeave = () => {
      srcRef.current = null;
      setPos(null);
    };
    container.addEventListener('pointerenter', refreshRect);
    container.addEventListener('pointermove', onMove);
    container.addEventListener('pointerleave', onLeave);
    window.addEventListener('scroll', refreshRect, true);
    return () => {
      ro.disconnect();
      container.removeEventListener('pointerenter', refreshRect);
      container.removeEventListener('pointermove', onMove);
      container.removeEventListener('pointerleave', onLeave);
      window.removeEventListener('scroll', refreshRect, true);
    };
  }, [active, bitmap, containerRef, stageRef]);

  // Repaint when the cleaned bitmap changes under a stationary cursor — e.g.
  // immediately after a region is added/removed by clicking.
  useEffect(() => {
    if (!active || !bitmap) return;
    const canvas = canvasRef.current;
    const src = srcRef.current;
    if (canvas && src) paint(canvas, bitmap, src);
  }, [bitmap, active]);

  if (!active || !pos) return null;
  return (
    <div
      className="pointer-events-none absolute z-20 overflow-hidden rounded-full border border-white/40 bg-background/40 shadow-lg"
      style={{
        left: pos.x + OFFSET,
        top: pos.y + OFFSET,
        width: LOUPE_SIZE,
        height: LOUPE_SIZE,
        imageRendering: 'pixelated',
      }}
    >
      <canvas ref={canvasRef} width={LOUPE_SIZE} height={LOUPE_SIZE} />
    </div>
  );
}
